import {IDataService} from "@/shared/DataService/IDataService";
import LocalDataStore from "@/shared/DataService/localDataStore";
import {Plex} from "@/shared/DataService/plex";
import AppSettings from "@/shared/Configuration/appSettings";
import {BackendType} from "@/shared/DataService/BackendType";
import NeoDataService from "@/shared/DataService/neoDataService";
import LocalDataService from "@/shared/DataService/localDataService";
import {IRootModel} from "@/shared/models/abstract/IRootModel";
import {INodeModel} from "@/shared/models/abstract/INodeModel";
import * as _ from "lodash";
import {IEdgeModel} from "@/shared/models/abstract/IEdgeModel";

/**
 * Data and settings functionality.
 */
export default class Configuration {
    /**
     * Sets up the redux store in function of the backend.
     */
    public static async init($store) {
        const settings = await Configuration.getAppSettingsFromLocalStore();
        if (settings.backend === BackendType.LocalStore) {
            await Configuration.setInitialPlexFromLocalStore($store);
        }
        await Configuration.fetchRecentItems($store);
        await Configuration.setAppSettingsInReduxStore($store, settings);
    }

    /**
     * Saves the settings to the redux store and to the local storage.
     */
    public static async setAndSaveSettings($store, settings) {
        await $store.dispatch("upsertSettings", settings);
        const dataService = new LocalDataStore();
        await dataService.upsertAppSettings(settings);
    }

    /**
     * Generic method to get the data service in function of the backend settings.
     */
    public static async getDataService($store): Promise<IDataService> {
        const settings = await Configuration.getAppSettingsFromLocalStore();
        switch (settings.backend) {
            case BackendType.LocalStore:
                return new LocalDataService($store);
            case BackendType.Neo4j:
                return new NeoDataService();
            default:
                throw new Error(`Data service '${settings.backend}' is not known.`)
        }
    }

    private static async fetchRecentItems($store) {
        const plex = $store.state.plex;
        const recentItems = await Configuration.convertRecentIds($store, plex.recent);
        await $store.dispatch("setRecent", recentItems);
    }

    /**
     * Sets the given settings in the redux store.
     */
    private static async setAppSettingsInReduxStore($store, settings) {
        await $store.dispatch("upsertSettings", settings);
    }

    /**
     * If using the local storage, all of the data sits in the redux store.
     */
    private static async setInitialPlexFromLocalStore($store): Promise<Plex> {
        const localStore = new LocalDataStore();
        const exists = await localStore.plexIdExists("default");
        let plex: Plex = null;
        if (exists) {
            plex = await localStore.getPlex("default");
        } else {
            plex = new Plex("default", "Default plex", "default");
            await localStore.upsertPlex(plex);
        }

        await $store.dispatch("upsertPlex", plex);
        return plex;
    }

    private static async convertRecentIds($store, recentIds: string[]) {
        const items = [];
        const dataService = await Configuration.getDataService($store);
        for (let i = 0; i < recentIds.length; i++) {
            let id: string = recentIds[i];
            let found: IRootModel = await dataService.getNodeById(id);
            if (_.isNil(found)) { // just can't know whether it's an edge or a node
                found = await dataService.getEdgeById(id);
            }
            if (_.isNil(found)) {
                continue;
            }
            items.push(found);
        }
        return items;
    }

    /**
     * Returns the app settings from the local storage.
     */
    private static async getAppSettingsFromLocalStore(): Promise<AppSettings> {
        const locaStore = new LocalDataStore();
        const exist = await locaStore.appSettingsExist();
        if (exist) {
            return await locaStore.getAppSettings();
        } else {
            const settings = new AppSettings();
            await locaStore.upsertAppSettings(settings);
            return settings;
        }
    }
}
